// src/components/forms/shared/DisplayFieldVariants.tsx
// Reusable DisplayField wrapper components with preset configurations
import React from 'react';
import { Box, Typography, useTheme } from '@mui/material';
import { ContentCopy as ContentCopyIcon } from '@mui/icons-material';
import { DisplayField, DisplayFieldProps } from './DisplayField';
import {
  useDisplayFieldPresets,
  mergePresetWithOverrides,
} from './DisplayFieldPresets';
import { CopyButton } from '../../lists/BeingsList';
import EventToast from '../../common/EventToast';
import { NotProvided } from '../../common/NotProvided';
import { useCopyToClipboard } from '../../../hooks/useCopyToClipboard';
import { styled } from '@mui/material/styles';
import { colors } from '../../../theme/theme';

type OverrideProps = Omit<DisplayFieldProps, 'value' | 'label'>;

interface CommonFieldProps extends Partial<OverrideProps> {
  label: string;
  value: string | React.ReactNode;
  startIcon?: React.ReactNode;
  endIcon?: React.ReactNode;
  copyable?: boolean;
  emptyText?: string;
}

const IconContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  margin: '0 0.5rem',
  color:
    theme.palette.mode === 'dark' ? colors.grey[0] : theme.palette.primary.main,
  '& .MuiSvgIcon-root': {
    fontSize: '2.4rem',
  },
}));

const useValueRenderer = () => {
  const theme = useTheme();
  const { showToast, toastPosition, handleCopy, hideToast } =
    useCopyToClipboard();

  const renderValue = (
    value: string | React.ReactNode,
    startIcon?: React.ReactNode,
    endIcon?: React.ReactNode,
    copyable?: boolean,
    emptyText?: string,
    hideLabel?: boolean
  ) => {
    const isSimpleValue =
      typeof value === 'string' || typeof value === 'number';
    const stringValue = isSimpleValue ? String(value) : '';
    const hasValue = stringValue && stringValue.trim() !== '';

    if (isSimpleValue && !hasValue) {
      return (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            width: '100%',
            minWidth: 0,
          }}
        >
          {startIcon && hideLabel && <IconContainer>{startIcon}</IconContainer>}
          <NotProvided type="NotProvided">{emptyText}</NotProvided>
        </Box>
      );
    }

    if (!isSimpleValue && value != null) {
      return (
        <>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              width: '100%',
              minWidth: 0,
            }}
          >
            {startIcon && hideLabel && (
              <IconContainer>{startIcon}</IconContainer>
            )}
            <Box sx={{ flex: 1, minWidth: 0 }}>{value}</Box>
            {endIcon && <IconContainer>{endIcon}</IconContainer>}
            {copyable && hasValue && (
              <CopyButton
                onClick={(e: React.MouseEvent) => handleCopy(stringValue, e)}
                size="small"
                aria-label="Copy value"
              >
                <ContentCopyIcon fontSize="small" />
              </CopyButton>
            )}
          </Box>
          <EventToast
            show={showToast}
            position={toastPosition}
            onClose={hideToast}
          />
        </>
      );
    }

    return (
      <>
        <Box
          display="flex"
          alignItems="center"
          gap="8px"
          sx={{
            whiteSpace: 'nowrap',
            overflowX: 'auto',
            overflowY: 'visible',
            WebkitOverflowScrolling: 'touch',
            scrollbarWidth: 'none',
            msOverflowStyle: 'none',
            width: '100%',
            minWidth: 0,
            '&::-webkit-scrollbar': { display: 'none' },
            '@media (max-width: 600px)': { paddingRight: '1rem' },
          }}
        >
          {startIcon && hideLabel && <IconContainer>{startIcon}</IconContainer>}
          <Typography
            sx={{
              fontSize: '2rem',
              fontWeight: 500,
              color: theme.palette.text.primary,
            }}
          >
            {value}
          </Typography>
          {endIcon && <IconContainer>{endIcon}</IconContainer>}
          {copyable && hasValue && (
            <CopyButton
              onClick={(e: React.MouseEvent) => handleCopy(stringValue, e)}
              size="small"
              aria-label="Copy value"
            >
              <ContentCopyIcon fontSize="small" />
            </CopyButton>
          )}
        </Box>
        <EventToast
          show={showToast}
          position={toastPosition}
          onClose={hideToast}
        />
      </>
    );
  };

  return { renderValue };
};

export const BeingDetailsField: React.FC<CommonFieldProps> = ({
  label,
  value,
  startIcon,
  endIcon,
  copyable,
  emptyText,
  ...overrides
}) => {
  const presets = useDisplayFieldPresets();
  const config = mergePresetWithOverrides(presets.beingDetails(), overrides);
  const { renderValue } = useValueRenderer();

  return (
    <DisplayField
      label={label}
      value={renderValue(
        value,
        startIcon,
        endIcon,
        copyable,
        emptyText,
        config.hideLabel
      )}
      hoverHighlight
      {...config}
    />
  );
};

export const BeingHeaderField: React.FC<CommonFieldProps> = ({
  label,
  value,
  startIcon,
  endIcon,
  copyable,
  emptyText,
  ...overrides
}) => {
  const presets = useDisplayFieldPresets();
  const config = mergePresetWithOverrides(presets.beingHeader(), overrides);
  const { renderValue } = useValueRenderer();

  return (
    <DisplayField
      label={label}
      value={renderValue(
        value,
        startIcon,
        endIcon,
        copyable,
        emptyText,
        config.hideLabel
      )}
      hoverHighlight
      {...config}
    />
  );
};

export const MetadataField: React.FC<CommonFieldProps> = ({
  label,
  value,
  startIcon,
  endIcon,
  copyable,
  emptyText,
  ...overrides
}) => {
  const presets = useDisplayFieldPresets();
  const config = mergePresetWithOverrides(presets.metadata(), overrides);
  const { renderValue } = useValueRenderer();

  return (
    <DisplayField
      label={label}
      value={renderValue(
        value,
        startIcon,
        endIcon,
        copyable,
        emptyText,
        config.hideLabel
      )}
      {...config}
    />
  );
};
